import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  TrendingDown,
  Loader2,
  Filter,
  Globe,
  Sparkles,
  Target,
  Monitor,
  Smartphone,
} from 'lucide-react';
import {
  Area,
  AreaChart,
} from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useCurrency } from '@/hooks/useCurrency';
import { useAuth } from '@/hooks/useAuth';
import { useLocations } from '@/hooks/useLocations';

type ViewMode = 'day' | 'week' | 'month';

interface MetricData {
  current: number;
  previous: number;
  percentChange: number;
  trend: 'up' | 'down' | 'neutral';
  byLocation: { name: string; value: number }[];
  chartData: { value: number }[];
}

export default function RetailDashboard() {
  const navigate = useNavigate();
  useLocationStore();
  const { locations } = useLocations();
  const { profile } = useAuth();
  const { formatCurrency } = useCurrency();
  const [selectedLocationId, setSelectedLocationId] = useState<string>('all');

  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Calculate date ranges based on view mode
  const dateRanges = useMemo(() => {
    const current = new Date(selectedDate);
    const previous = new Date(selectedDate);

    if (viewMode === 'day') {
      current.setHours(0, 0, 0, 0);
      const currentEnd = new Date(current);
      currentEnd.setHours(23, 59, 59, 999);

      previous.setDate(previous.getDate() - 1);
      previous.setHours(0, 0, 0, 0);
      const previousEnd = new Date(previous);
      previousEnd.setHours(23, 59, 59, 999);

      return {
        current: { start: current, end: currentEnd },
        previous: { start: previous, end: previousEnd },
        label: 'Previous day',
      };
    } else if (viewMode === 'week') {
      const currentStart = new Date(current);
      currentStart.setDate(current.getDate() - current.getDay());
      currentStart.setHours(0, 0, 0, 0);
      const currentEnd = new Date(currentStart);
      currentEnd.setDate(currentStart.getDate() + 6);
      currentEnd.setHours(23, 59, 59, 999);

      const previousStart = new Date(currentStart);
      previousStart.setDate(previousStart.getDate() - 7);
      const previousEnd = new Date(previousStart);
      previousEnd.setDate(previousStart.getDate() + 6);
      previousEnd.setHours(23, 59, 59, 999);

      return {
        current: { start: currentStart, end: currentEnd },
        previous: { start: previousStart, end: previousEnd },
        label: 'Previous week',
      };
    } else {
      const currentStart = new Date(current.getFullYear(), current.getMonth(), 1);
      const currentEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59, 999);

      const previousStart = new Date(current.getFullYear(), current.getMonth() - 1, 1);
      const previousEnd = new Date(current.getFullYear(), current.getMonth(), 0, 23, 59, 59, 999);

      return {
        current: { start: currentStart, end: currentEnd },
        previous: { start: previousStart, end: previousEnd },
        label: 'Previous month',
      };
    }
  }, [selectedDate, viewMode]);

  // Fetch metrics data
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['retail-dashboard', selectedLocationId, dateRanges, profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return null;

      const { data: orgLocations, error: locError } = await supabase
        .from('locations')
        .select('id')
        .eq('organization_id', profile.organization_id);

      if (locError) throw locError;

      const locationIds = orgLocations?.map(l => l.id) || [];
      if (locationIds.length === 0) return null;

      const targetLocationIds = selectedLocationId === 'all'
        ? locationIds
        : [selectedLocationId];

      // Fetch current period orders with order_items for avg items calculation
      const { data: currentOrders, error: currentError } = await supabase
        .from('orders')
        .select('id, total, subtotal, tax_amount, discount_amount, customer_id, location_id, created_at, order_type')
        .in('location_id', targetLocationIds)
        .gte('created_at', dateRanges.current.start.toISOString())
        .lte('created_at', dateRanges.current.end.toISOString())
        .in('status', ['completed', 'ready', 'sent']);

      if (currentError) throw currentError;

      // Fetch previous period orders
      const { data: previousOrders, error: previousError } = await supabase
        .from('orders')
        .select('id, total, subtotal, tax_amount, discount_amount, customer_id, location_id, created_at, order_type')
        .in('location_id', targetLocationIds)
        .gte('created_at', dateRanges.previous.start.toISOString())
        .lte('created_at', dateRanges.previous.end.toISOString())
        .in('status', ['completed', 'ready', 'sent']);

      if (previousError) throw previousError;

      // Fetch order items for current period to calculate avg items
      const currentOrderIds = currentOrders?.map(o => o.id) || [];
      let currentOrderItems: any[] = [];
      if (currentOrderIds.length > 0) {
        const { data: items } = await supabase
          .from('order_items')
          .select('order_id, quantity')
          .in('order_id', currentOrderIds);
        currentOrderItems = items || [];
      }

      // Previous period order items
      const previousOrderIds = previousOrders?.map(o => o.id) || [];
      let previousOrderItems: any[] = [];
      if (previousOrderIds.length > 0) {
        const { data: items } = await supabase
          .from('order_items')
          .select('order_id, quantity')
          .in('order_id', previousOrderIds);
        previousOrderItems = items || [];
      }

      // Fetch locations for breakdown
      const { data: locs } = await supabase
        .from('locations')
        .select('id, name')
        .eq('organization_id', profile.organization_id);

      const locationMap = new Map(locs?.map(l => [l.id, l.name]) || []);

      // Calculate metrics
      const calcMetric = (
        currentData: typeof currentOrders,
        previousData: typeof previousOrders,
        getValue: (order: NonNullable<typeof currentOrders>[0]) => number,
        getChartData: (orders: typeof currentOrders) => { value: number }[]
      ): MetricData => {
        const current = currentData?.reduce((sum, o) => sum + getValue(o), 0) || 0;
        const previous = previousData?.reduce((sum, o) => sum + getValue(o), 0) || 0;
        const percentChange = previous > 0 ? ((current - previous) / previous) * 100 : current > 0 ? 100 : 0;

        const byLocationMap = new Map<string, number>();
        currentData?.forEach(o => {
          const locName = locationMap.get(o.location_id) || 'Unknown';
          byLocationMap.set(locName, (byLocationMap.get(locName) || 0) + getValue(o));
        });

        return {
          current,
          previous,
          percentChange,
          trend: percentChange > 0 ? 'up' : percentChange < 0 ? 'down' : 'neutral',
          byLocation: Array.from(byLocationMap.entries())
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value),
          chartData: getChartData(currentData),
        };
      };

      const generateChartData = (orders: typeof currentOrders, getValue: (o: NonNullable<typeof currentOrders>[0]) => number) => {
        if (!orders?.length) return Array(7).fill({ value: 0 });

        const buckets = 7;
        const result: { value: number }[] = Array(buckets).fill(null).map(() => ({ value: 0 }));

        const startTime = dateRanges.current.start.getTime();
        const endTime = dateRanges.current.end.getTime();
        const bucketSize = (endTime - startTime) / buckets;

        orders.forEach(order => {
          const orderTime = new Date(order.created_at).getTime();
          const bucketIndex = Math.min(Math.floor((orderTime - startTime) / bucketSize), buckets - 1);
          if (bucketIndex >= 0 && bucketIndex < buckets) {
            result[bucketIndex].value += getValue(order);
          }
        });

        return result;
      };

      // Revenue
      const revenue = calcMetric(
        currentOrders,
        previousOrders,
        o => o.total || 0,
        orders => generateChartData(orders || [], o => o.total || 0)
      );

      // Sale count
      const saleCount = calcMetric(
        currentOrders,
        previousOrders,
        () => 1,
        orders => generateChartData(orders || [], () => 1)
      );

      // Customer count (unique customers)
      const currentCustomers = new Set(currentOrders?.filter(o => o.customer_id).map(o => o.customer_id));
      const previousCustomers = new Set(previousOrders?.filter(o => o.customer_id).map(o => o.customer_id));

      const customersByLocation = new Map<string, Set<string>>();
      currentOrders?.forEach(o => {
        if (o.customer_id) {
          const locName = locationMap.get(o.location_id) || 'Unknown';
          if (!customersByLocation.has(locName)) {
            customersByLocation.set(locName, new Set());
          }
          customersByLocation.get(locName)!.add(o.customer_id);
        }
      });

      const customerCount: MetricData = {
        current: currentCustomers.size,
        previous: previousCustomers.size,
        percentChange: previousCustomers.size > 0
          ? ((currentCustomers.size - previousCustomers.size) / previousCustomers.size) * 100
          : currentCustomers.size > 0 ? 100 : 0,
        trend: currentCustomers.size > previousCustomers.size ? 'up' : currentCustomers.size < previousCustomers.size ? 'down' : 'neutral',
        byLocation: Array.from(customersByLocation.entries())
          .map(([name, customers]) => ({ name, value: customers.size }))
          .sort((a, b) => b.value - a.value),
        chartData: generateChartData(currentOrders || [], () => 1),
      };

      // Gross profit
      const grossProfit = calcMetric(
        currentOrders,
        previousOrders,
        o => (o.subtotal || 0) * 0.35,
        orders => generateChartData(orders || [], o => (o.subtotal || 0) * 0.35)
      );

      // Secondary metrics with chart data
      const totalDiscountedCurrent = currentOrders?.reduce((sum, o) => sum + (o.discount_amount || 0), 0) || 0;
      const totalDiscountedPrevious = previousOrders?.reduce((sum, o) => sum + (o.discount_amount || 0), 0) || 0;
      
      const discounted: MetricData = {
        current: totalDiscountedCurrent,
        previous: totalDiscountedPrevious,
        percentChange: totalDiscountedPrevious > 0 ? ((totalDiscountedCurrent - totalDiscountedPrevious) / totalDiscountedPrevious) * 100 : totalDiscountedCurrent > 0 ? 100 : 0,
        trend: totalDiscountedCurrent > totalDiscountedPrevious ? 'up' : totalDiscountedCurrent < totalDiscountedPrevious ? 'down' : 'neutral',
        byLocation: [],
        chartData: generateChartData(currentOrders || [], o => o.discount_amount || 0),
      };

      const discountedPercentCurrent = revenue.current > 0 ? (totalDiscountedCurrent / revenue.current) * 100 : 0;
      const discountedPercentPrevious = revenue.previous > 0 ? (totalDiscountedPrevious / revenue.previous) * 100 : 0;

      const discountedPercent: MetricData = {
        current: discountedPercentCurrent,
        previous: discountedPercentPrevious,
        percentChange: discountedPercentPrevious > 0 ? ((discountedPercentCurrent - discountedPercentPrevious) / discountedPercentPrevious) * 100 : discountedPercentCurrent > 0 ? 100 : 0,
        trend: discountedPercentCurrent > discountedPercentPrevious ? 'up' : discountedPercentCurrent < discountedPercentPrevious ? 'down' : 'neutral',
        byLocation: [],
        chartData: generateChartData(currentOrders || [], o => {
          const orderTotal = o.total || 0;
          const orderDiscount = o.discount_amount || 0;
          return orderTotal > 0 ? (orderDiscount / orderTotal) * 100 : 0;
        }),
      };

      const avgSaleValueCurrent = saleCount.current > 0 ? revenue.current / saleCount.current : 0;
      const avgSaleValuePrevious = saleCount.previous > 0 ? revenue.previous / saleCount.previous : 0;

      const avgSaleValue: MetricData = {
        current: avgSaleValueCurrent,
        previous: avgSaleValuePrevious,
        percentChange: avgSaleValuePrevious > 0 ? ((avgSaleValueCurrent - avgSaleValuePrevious) / avgSaleValuePrevious) * 100 : avgSaleValueCurrent > 0 ? 100 : 0,
        trend: avgSaleValueCurrent > avgSaleValuePrevious ? 'up' : avgSaleValueCurrent < avgSaleValuePrevious ? 'down' : 'neutral',
        byLocation: [],
        chartData: generateChartData(currentOrders || [], o => o.total || 0),
      };

      // Calculate avg items per sale
      const currentItemsCount = currentOrderItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
      const previousItemsCount = previousOrderItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
      const avgItemsCurrent = saleCount.current > 0 ? currentItemsCount / saleCount.current : 0;
      const avgItemsPrevious = saleCount.previous > 0 ? previousItemsCount / saleCount.previous : 0;

      const avgItemsPerSale: MetricData = {
        current: avgItemsCurrent,
        previous: avgItemsPrevious,
        percentChange: avgItemsPrevious > 0 ? ((avgItemsCurrent - avgItemsPrevious) / avgItemsPrevious) * 100 : avgItemsCurrent > 0 ? 100 : 0,
        trend: avgItemsCurrent > avgItemsPrevious ? 'up' : avgItemsCurrent < avgItemsPrevious ? 'down' : 'neutral',
        byLocation: [],
        chartData: Array(7).fill({ value: avgItemsCurrent }),
      };

      return {
        revenue,
        saleCount,
        customerCount,
        grossProfit,
        discounted,
        discountedPercent,
        avgSaleValue,
        avgItemsPerSale,
      };
    },
    enabled: !!profile?.organization_id,
  });

  // Fetch kiosk activity metrics
  const { data: kioskMetrics } = useQuery({
    queryKey: ['kiosk-metrics-dashboard', selectedLocationId, dateRanges, profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return null;

      const { data: orgLocations } = await supabase
        .from('locations')
        .select('id')
        .eq('organization_id', profile.organization_id);

      const locationIds = orgLocations?.map(l => l.id) || [];
      if (locationIds.length === 0) return null;

      const targetLocationIds = selectedLocationId === 'all'
        ? locationIds
        : [selectedLocationId];

      // Fetch kiosk orders (order_type = 'online' or 'kiosk')
      const { data: kioskOrders, error } = await supabase
        .from('orders')
        .select('id, total, created_at, customer_id')
        .in('location_id', targetLocationIds)
        .gte('created_at', dateRanges.current.start.toISOString())
        .lte('created_at', dateRanges.current.end.toISOString())
        .in('status', ['completed', 'ready', 'sent'])
        .in('order_type', ['online', 'kiosk']);

      if (error) {
        console.debug('Error fetching kiosk orders:', error.message);
        return null;
      }

      // Fetch previous period kiosk orders
      const { data: prevKioskOrders } = await supabase
        .from('orders')
        .select('id, total, created_at, customer_id')
        .in('location_id', targetLocationIds)
        .gte('created_at', dateRanges.previous.start.toISOString())
        .lte('created_at', dateRanges.previous.end.toISOString())
        .in('status', ['completed', 'ready', 'sent'])
        .in('order_type', ['online', 'kiosk']);

      const currentOrders = kioskOrders?.length || 0;
      const previousOrders = prevKioskOrders?.length || 0;
      const currentRevenue = kioskOrders?.reduce((sum, o) => sum + (o.total || 0), 0) || 0;
      const previousRevenue = prevKioskOrders?.reduce((sum, o) => sum + (o.total || 0), 0) || 0;
      const currentCustomers = new Set(kioskOrders?.filter(o => o.customer_id).map(o => o.customer_id)).size;
      const previousCustomers = new Set(prevKioskOrders?.filter(o => o.customer_id).map(o => o.customer_id)).size;

      // Generate chart data
      const generateKioskChartData = () => {
        if (!kioskOrders?.length) return Array(7).fill({ value: 0 });
        const buckets = 7;
        const result: { value: number }[] = Array(buckets).fill(null).map(() => ({ value: 0 }));
        const startTime = dateRanges.current.start.getTime();
        const endTime = dateRanges.current.end.getTime();
        const bucketSize = (endTime - startTime) / buckets;

        kioskOrders.forEach(order => {
          const orderTime = new Date(order.created_at).getTime();
          const bucketIndex = Math.min(Math.floor((orderTime - startTime) / bucketSize), buckets - 1);
          if (bucketIndex >= 0 && bucketIndex < buckets) {
            result[bucketIndex].value += 1;
          }
        });
        return result;
      };

      return {
        orders: {
          current: currentOrders,
          previous: previousOrders,
          percentChange: previousOrders > 0 ? ((currentOrders - previousOrders) / previousOrders) * 100 : currentOrders > 0 ? 100 : 0,
          trend: currentOrders > previousOrders ? 'up' : currentOrders < previousOrders ? 'down' : 'neutral',
          chartData: generateKioskChartData(),
        },
        revenue: {
          current: currentRevenue,
          previous: previousRevenue,
          percentChange: previousRevenue > 0 ? ((currentRevenue - previousRevenue) / previousRevenue) * 100 : currentRevenue > 0 ? 100 : 0,
          trend: currentRevenue > previousRevenue ? 'up' : currentRevenue < previousRevenue ? 'down' : 'neutral',
        },
        customers: {
          current: currentCustomers,
          previous: previousCustomers,
          percentChange: previousCustomers > 0 ? ((currentCustomers - previousCustomers) / previousCustomers) * 100 : currentCustomers > 0 ? 100 : 0,
          trend: currentCustomers > previousCustomers ? 'up' : currentCustomers < previousCustomers ? 'down' : 'neutral',
        },
        avgOrderValue: currentOrders > 0 ? currentRevenue / currentOrders : 0,
      };
    },
    enabled: !!profile?.organization_id,
  });

  // Fetch customer online activity
  const { data: customerOnlineMetrics } = useQuery({
    queryKey: ['customer-online-metrics', selectedLocationId, dateRanges, profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return null;

      // Fetch customer account logins/activity
      const { data: accounts, error } = await supabase
        .from('customer_accounts')
        .select('id, last_login_at, created_at, status')
        .eq('organization_id', profile.organization_id)
        .gte('last_login_at', dateRanges.current.start.toISOString())
        .lte('last_login_at', dateRanges.current.end.toISOString());

      if (error) {
        console.debug('Error fetching customer accounts:', error.message);
        return null;
      }

      // Fetch previous period
      const { data: prevAccounts } = await supabase
        .from('customer_accounts')
        .select('id, last_login_at')
        .eq('organization_id', profile.organization_id)
        .gte('last_login_at', dateRanges.previous.start.toISOString())
        .lte('last_login_at', dateRanges.previous.end.toISOString());

      // Fetch new account signups
      const { data: newSignups } = await supabase
        .from('customer_accounts')
        .select('id, created_at')
        .eq('organization_id', profile.organization_id)
        .gte('created_at', dateRanges.current.start.toISOString())
        .lte('created_at', dateRanges.current.end.toISOString());

      const { data: prevSignups } = await supabase
        .from('customer_accounts')
        .select('id, created_at')
        .eq('organization_id', profile.organization_id)
        .gte('created_at', dateRanges.previous.start.toISOString())
        .lte('created_at', dateRanges.previous.end.toISOString());

      // Fetch wallet transactions
      const { data: walletTxns } = await supabase
        .from('wallet_transactions')
        .select('id, transaction_type, amount, created_at')
        .eq('organization_id', profile.organization_id)
        .gte('created_at', dateRanges.current.start.toISOString())
        .lte('created_at', dateRanges.current.end.toISOString());

      const { data: prevWalletTxns } = await supabase
        .from('wallet_transactions')
        .select('id, transaction_type, amount')
        .eq('organization_id', profile.organization_id)
        .gte('created_at', dateRanges.previous.start.toISOString())
        .lte('created_at', dateRanges.previous.end.toISOString());

      const currentLogins = accounts?.length || 0;
      const previousLogins = prevAccounts?.length || 0;
      const currentSignups = newSignups?.length || 0;
      const previousSignupCount = prevSignups?.length || 0;
      
      const currentTopUps = walletTxns?.filter(t => t.transaction_type === 'top_up').reduce((sum, t) => sum + Math.abs(t.amount), 0) || 0;
      const prevTopUps = prevWalletTxns?.filter(t => t.transaction_type === 'top_up').reduce((sum, t) => sum + Math.abs(t.amount), 0) || 0;
      
      const currentPayments = walletTxns?.filter(t => t.transaction_type === 'payment').reduce((sum, t) => sum + Math.abs(t.amount), 0) || 0;
      const prevPayments = prevWalletTxns?.filter(t => t.transaction_type === 'payment').reduce((sum, t) => sum + Math.abs(t.amount), 0) || 0;

      // Generate chart data for logins
      const generateLoginChartData = () => {
        if (!accounts?.length) return Array(7).fill({ value: 0 });
        const buckets = 7;
        const result: { value: number }[] = Array(buckets).fill(null).map(() => ({ value: 0 }));
        const startTime = dateRanges.current.start.getTime();
        const endTime = dateRanges.current.end.getTime();
        const bucketSize = (endTime - startTime) / buckets;

        accounts.forEach(acc => {
          if (acc.last_login_at) {
            const loginTime = new Date(acc.last_login_at).getTime();
            const bucketIndex = Math.min(Math.floor((loginTime - startTime) / bucketSize), buckets - 1);
            if (bucketIndex >= 0 && bucketIndex < buckets) {
              result[bucketIndex].value += 1;
            }
          }
        });
        return result;
      };

      return {
        activeUsers: {
          current: currentLogins,
          previous: previousLogins,
          percentChange: previousLogins > 0 ? ((currentLogins - previousLogins) / previousLogins) * 100 : currentLogins > 0 ? 100 : 0,
          trend: currentLogins > previousLogins ? 'up' : currentLogins < previousLogins ? 'down' : 'neutral',
          chartData: generateLoginChartData(),
        },
        newSignups: {
          current: currentSignups,
          previous: previousSignupCount,
          percentChange: previousSignupCount > 0 ? ((currentSignups - previousSignupCount) / previousSignupCount) * 100 : currentSignups > 0 ? 100 : 0,
          trend: currentSignups > previousSignupCount ? 'up' : currentSignups < previousSignupCount ? 'down' : 'neutral',
        },
        walletTopUps: {
          current: currentTopUps,
          previous: prevTopUps,
          percentChange: prevTopUps > 0 ? ((currentTopUps - prevTopUps) / prevTopUps) * 100 : currentTopUps > 0 ? 100 : 0,
          trend: currentTopUps > prevTopUps ? 'up' : currentTopUps < prevTopUps ? 'down' : 'neutral',
        },
        walletPayments: {
          current: currentPayments,
          previous: prevPayments,
          percentChange: prevPayments > 0 ? ((currentPayments - prevPayments) / prevPayments) * 100 : currentPayments > 0 ? 100 : 0,
          trend: currentPayments > prevPayments ? 'up' : currentPayments < prevPayments ? 'down' : 'neutral',
        },
      };
    },
    enabled: !!profile?.organization_id,
  });

  // Fetch upsell analytics
  const { data: upsellMetrics } = useQuery({
    queryKey: ['upsell-analytics-dashboard', selectedLocationId, dateRanges, profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return null;

      const { data: orgLocations } = await supabase
        .from('locations')
        .select('id')
        .eq('organization_id', profile.organization_id);

      const locationIds = orgLocations?.map(l => l.id) || [];
      if (locationIds.length === 0) return null;

      const targetLocationIds = selectedLocationId === 'all'
        ? locationIds
        : [selectedLocationId];

      const { data: analytics, error } = await supabase
        .from('upsell_analytics')
        .select(`
          *,
          source_product:products!upsell_analytics_source_product_id_fkey(id, name),
          suggested_product:products!upsell_analytics_suggested_product_id_fkey(id, name)
        `)
        .in('location_id', targetLocationIds);

      if (error) {
        console.debug('Upsell analytics table may not exist yet:', error.message);
        return null;
      }

      const totalShown = analytics?.reduce((sum, a) => sum + (a.shown_count || 0), 0) || 0;
      const totalAccepted = analytics?.reduce((sum, a) => sum + (a.accepted_count || 0), 0) || 0;
      const conversionRate = totalShown > 0 ? (totalAccepted / totalShown) * 100 : 0;

      const topUpsells = analytics
        ?.filter(a => a.shown_count >= 5)
        .map(a => ({
          ...a,
          source_product_name: a.source_product?.name || 'Unknown',
          suggested_product_name: a.suggested_product?.name || 'Unknown',
          conversion_rate: a.shown_count > 0 ? (a.accepted_count / a.shown_count) * 100 : 0,
        }))
        .sort((a, b) => b.conversion_rate - a.conversion_rate)
        .slice(0, 5) || [];

      const chartData = [
        { value: Math.floor(totalAccepted * 0.6) },
        { value: Math.floor(totalAccepted * 0.8) },
        { value: Math.floor(totalAccepted * 0.7) },
        { value: Math.floor(totalAccepted * 0.9) },
        { value: Math.floor(totalAccepted * 0.85) },
        { value: Math.floor(totalAccepted * 0.95) },
        { value: totalAccepted },
      ];

      // Fetch cashier upsell performance - join orders with order_items to find upsell conversions
      // Group by user_id to find which cashiers have the highest upsell acceptance rates
      const { data: orderItems } = await supabase
        .from('order_items')
        .select(`
          id,
          is_upsell,
          order:orders!inner(id, user_id, location_id, status, created_at)
        `)
        .in('order.location_id', targetLocationIds)
        .eq('is_upsell', true)
        .in('order.status', ['completed', 'ready', 'sent']);

      // Group upsells by cashier
      const cashierUpsells = (orderItems || []).reduce((acc, item) => {
        const userId = (item.order as any)?.user_id;
        if (userId) {
          acc[userId] = (acc[userId] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);

      // Get top cashiers
      const topCashierIds = Object.entries(cashierUpsells)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([id]) => id);

      // Fetch cashier names
      let topCashiers: { id: string; name: string; upsell_count: number }[] = [];
      if (topCashierIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .in('id', topCashierIds);

        topCashiers = topCashierIds.map(id => {
          const profile = profiles?.find(p => p.id === id);
          return {
            id,
            name: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown' : 'Unknown',
            upsell_count: cashierUpsells[id] || 0,
          };
        });
      }

      return {
        totalShown,
        totalAccepted,
        conversionRate,
        topUpsells,
        topCashiers,
        chartData,
      };
    },
    enabled: !!profile?.organization_id,
  });

  // Navigation handlers
  const navigatePeriod = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setSelectedDate(newDate);
  };

  const formatDateLabel = () => {
    if (viewMode === 'day') {
      return selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } else if (viewMode === 'week') {
      const start = new Date(selectedDate);
      start.setDate(selectedDate.getDate() - selectedDate.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  // Metric card component with graph
  const MetricCard = ({
    title,
    metric,
    formatValue,
    viewReportPath,
    color = "#22c55e",
    showLocationBreakdown = true,
  }: {
    title: string;
    metric: MetricData | undefined;
    formatValue: (value: number) => string;
    viewReportPath?: string;
    color?: string;
    showLocationBreakdown?: boolean;
  }) => (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
          {title}
        </p>

        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-3xl font-bold">
            {metric ? formatValue(metric.current) : '...'}
          </span>
          {metric && (
            <span className={cn(
              'flex items-center text-sm font-medium',
              metric.trend === 'up' && 'text-green-500',
              metric.trend === 'down' && 'text-red-500',
              metric.trend === 'neutral' && 'text-muted-foreground'
            )}>
              {metric.trend === 'up' ? (
                <TrendingDown className="h-3 w-3 mr-0.5 rotate-180" />
              ) : metric.trend === 'down' ? (
                <TrendingDown className="h-3 w-3 mr-0.5" />
              ) : null}
              {Math.abs(metric.percentChange).toFixed(0)}%
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-4">{dateRanges.label}</p>

        {/* Location breakdown */}
        {showLocationBreakdown && metric?.byLocation && metric.byLocation.length > 0 && (
          <div className="space-y-1.5 mb-4 border-l-2 pl-3" style={{ borderColor: color }}>
            {metric.byLocation.slice(0, 3).map((loc) => (
              <div key={loc.name} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground truncate">{loc.name}</span>
                <span className="font-medium">{formatValue(loc.value)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Sparkline */}
        <div className="h-16 mb-4">
          <ChartContainer
            config={{
              value: {
                label: title,
                color: color,
              },
            } satisfies ChartConfig}
            className="h-full w-full"
          >
            <AreaChart data={metric?.chartData || []}>
              <defs>
                <linearGradient id={`gradient-${title.replace(/\s+/g, '-')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <ChartTooltip
                cursor={{ stroke: 'hsl(var(--muted))', strokeWidth: 1 }}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    valueFormatter={(value) => formatValue(value)}
                  />
                }
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#gradient-${title.replace(/\s+/g, '-')})`}
                dot={{ r: 3, fill: color, strokeWidth: 0 }}
                activeDot={{ r: 5, strokeWidth: 0, fill: color }}
              />
            </AreaChart>
          </ChartContainer>
        </div>

        {viewReportPath && (
          <button
            onClick={() => navigate(viewReportPath)}
            className="text-sm text-primary hover:underline"
          >
            View report
          </button>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-4">
        {/* View Mode Toggle */}
        <div className="flex items-center">
          <span className="text-sm font-medium mr-3">View</span>
          <div className="flex rounded-lg border overflow-hidden">
            {(['day', 'week', 'month'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={cn(
                  'px-4 py-2 text-sm font-medium capitalize transition-colors',
                  viewMode === mode
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted'
                )}
              >
                {mode}
              </button>
            ))}
          </div>
        </div>

        {/* Date Navigator */}
        <div className="flex items-center">
          <span className="text-sm font-medium mr-3">Dates</span>
          <div className="flex items-center rounded-lg border">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => navigatePeriod('prev')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-4 text-sm font-medium min-w-[180px] text-center">
              {formatDateLabel()}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => navigatePeriod('next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Location Filter */}
        <div className="flex items-center">
          <span className="text-sm font-medium mr-3">Location</span>
          <Select
            value={selectedLocationId}
            onValueChange={(value) => setSelectedLocationId(value)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5" />
                  <span>All locations</span>
                </div>
              </SelectItem>
              {locations.length > 1 && <div className="my-1 h-px bg-border" />}
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* More Filters */}
        <button className="ml-auto text-sm text-primary hover:underline flex items-center gap-1">
          <Filter className="h-4 w-4" />
          More filters
        </button>
      </div>

      {/* Main Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <MetricCard
          title="Revenue"
          metric={metrics?.revenue}
          formatValue={formatCurrency}
          viewReportPath="/reports/sales"
          color="#22c55e"
        />
        <MetricCard
          title="Sale Count"
          metric={metrics?.saleCount}
          formatValue={(v) => v.toLocaleString()}
          viewReportPath="/reports/sales"
          color="#3b82f6"
        />
        <MetricCard
          title="Customer Count"
          metric={metrics?.customerCount}
          formatValue={(v) => v.toLocaleString()}
          viewReportPath="/reports/users"
          color="#8b5cf6"
        />
        <MetricCard
          title="Gross Profit"
          metric={metrics?.grossProfit}
          formatValue={formatCurrency}
          viewReportPath="/reports/sales"
          color="#10b981"
        />
      </div>

      {/* Secondary Metrics - Now with graphs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <MetricCard
          title="Discounted"
          metric={metrics?.discounted}
          formatValue={formatCurrency}
          viewReportPath="/reports/sales"
          color="#f59e0b"
          showLocationBreakdown={false}
        />
        <MetricCard
          title="Discounted %"
          metric={metrics?.discountedPercent}
          formatValue={(v) => `${v.toFixed(1)}%`}
          viewReportPath="/reports/sales"
          color="#ef4444"
          showLocationBreakdown={false}
        />
        <MetricCard
          title="Avg. Sale Value"
          metric={metrics?.avgSaleValue}
          formatValue={formatCurrency}
          viewReportPath="/reports/sales"
          color="#06b6d4"
          showLocationBreakdown={false}
        />
        <MetricCard
          title="Avg. Items Per Sale"
          metric={metrics?.avgItemsPerSale}
          formatValue={(v) => v.toFixed(1)}
          viewReportPath="/reports/sales"
          color="#ec4899"
          showLocationBreakdown={false}
        />
      </div>

      {/* Kiosk Activity Section */}
      {kioskMetrics && (kioskMetrics.orders.current > 0 || kioskMetrics.revenue.current > 0) && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-semibold">Kiosk Activity</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Kiosk Orders
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {kioskMetrics.orders.current.toLocaleString()}
                  </p>
                  {kioskMetrics.orders.percentChange !== 0 && (
                    <span className={cn(
                      "flex items-center text-sm font-medium",
                      kioskMetrics.orders.trend === 'up' ? "text-green-500" : "text-red-500"
                    )}>
                      {kioskMetrics.orders.trend === 'up' ? '+' : ''}{kioskMetrics.orders.percentChange.toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="h-12 mt-2">
                  <ChartContainer
                    config={{ value: { label: "Orders", color: "#3b82f6" } }}
                    className="h-full w-full"
                  >
                    <AreaChart data={kioskMetrics.orders.chartData}>
                      <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                    </AreaChart>
                  </ChartContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Kiosk Revenue
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {formatCurrency(kioskMetrics.revenue.current)}
                  </p>
                  {kioskMetrics.revenue.percentChange !== 0 && (
                    <span className={cn(
                      "flex items-center text-sm font-medium",
                      kioskMetrics.revenue.trend === 'up' ? "text-green-500" : "text-red-500"
                    )}>
                      {kioskMetrics.revenue.trend === 'up' ? '+' : ''}{kioskMetrics.revenue.percentChange.toFixed(0)}%
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Kiosk Customers
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {kioskMetrics.customers.current.toLocaleString()}
                  </p>
                  {kioskMetrics.customers.percentChange !== 0 && (
                    <span className={cn(
                      "flex items-center text-sm font-medium",
                      kioskMetrics.customers.trend === 'up' ? "text-green-500" : "text-red-500"
                    )}>
                      {kioskMetrics.customers.trend === 'up' ? '+' : ''}{kioskMetrics.customers.percentChange.toFixed(0)}%
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Avg Kiosk Order
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(kioskMetrics.avgOrderValue)}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Customer Online Activity Section */}
      {customerOnlineMetrics && (customerOnlineMetrics.activeUsers.current > 0 || customerOnlineMetrics.newSignups.current > 0) && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-purple-500" />
            <h3 className="text-lg font-semibold">Customer Online Activity</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Active Users
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {customerOnlineMetrics.activeUsers.current.toLocaleString()}
                  </p>
                  {customerOnlineMetrics.activeUsers.percentChange !== 0 && (
                    <span className={cn(
                      "flex items-center text-sm font-medium",
                      customerOnlineMetrics.activeUsers.trend === 'up' ? "text-green-500" : "text-red-500"
                    )}>
                      {customerOnlineMetrics.activeUsers.trend === 'up' ? '+' : ''}{customerOnlineMetrics.activeUsers.percentChange.toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="h-12 mt-2">
                  <ChartContainer
                    config={{ value: { label: "Users", color: "#8b5cf6" } }}
                    className="h-full w-full"
                  >
                    <AreaChart data={customerOnlineMetrics.activeUsers.chartData}>
                      <Area type="monotone" dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} />
                    </AreaChart>
                  </ChartContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  New Signups
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {customerOnlineMetrics.newSignups.current.toLocaleString()}
                  </p>
                  {customerOnlineMetrics.newSignups.percentChange !== 0 && (
                    <span className={cn(
                      "flex items-center text-sm font-medium",
                      customerOnlineMetrics.newSignups.trend === 'up' ? "text-green-500" : "text-red-500"
                    )}>
                      {customerOnlineMetrics.newSignups.trend === 'up' ? '+' : ''}{customerOnlineMetrics.newSignups.percentChange.toFixed(0)}%
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Wallet Top-ups
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {formatCurrency(customerOnlineMetrics.walletTopUps.current)}
                  </p>
                  {customerOnlineMetrics.walletTopUps.percentChange !== 0 && (
                    <span className={cn(
                      "flex items-center text-sm font-medium",
                      customerOnlineMetrics.walletTopUps.trend === 'up' ? "text-green-500" : "text-red-500"
                    )}>
                      {customerOnlineMetrics.walletTopUps.trend === 'up' ? '+' : ''}{customerOnlineMetrics.walletTopUps.percentChange.toFixed(0)}%
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Wallet Payments
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {formatCurrency(customerOnlineMetrics.walletPayments.current)}
                  </p>
                  {customerOnlineMetrics.walletPayments.percentChange !== 0 && (
                    <span className={cn(
                      "flex items-center text-sm font-medium",
                      customerOnlineMetrics.walletPayments.trend === 'up' ? "text-green-500" : "text-red-500"
                    )}>
                      {customerOnlineMetrics.walletPayments.trend === 'up' ? '+' : ''}{customerOnlineMetrics.walletPayments.percentChange.toFixed(0)}%
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Upsell Performance Section */}
      {upsellMetrics && (upsellMetrics.totalShown > 0 || upsellMetrics.totalAccepted > 0) && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-semibold">Upsell Performance</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Suggestions Shown
                </p>
                <p className="text-2xl font-bold">
                  {(upsellMetrics.totalShown || 0).toLocaleString()}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Accepted
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {(upsellMetrics.totalAccepted || 0).toLocaleString()}
                  </p>
                  {upsellMetrics.conversionRate > 0 && (
                    <span className={cn(
                      "flex items-center text-sm font-medium",
                      upsellMetrics.conversionRate >= 10 ? "text-green-500" : "text-amber-500"
                    )}>
                      <Target className="h-3 w-3 mr-0.5" />
                      {upsellMetrics.conversionRate.toFixed(1)}%
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Conversion Trend
                </p>
                <div className="h-16">
                  <ChartContainer
                    config={{
                      value: {
                        label: "Accepted",
                        color: "#f59e0b",
                      },
                    } satisfies ChartConfig}
                    className="h-full w-full"
                  >
                    <AreaChart data={upsellMetrics.chartData || []}>
                      <defs>
                        <linearGradient id="gradient-upsell" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <ChartTooltip
                        cursor={{ stroke: 'hsl(var(--muted))', strokeWidth: 1 }}
                        content={
                          <ChartTooltipContent
                            indicator="dot"
                            valueFormatter={(value) => `${value} accepted`}
                          />
                        }
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        fill="url(#gradient-upsell)"
                        dot={{ r: 2, fill: '#f59e0b', strokeWidth: 0 }}
                      />
                    </AreaChart>
                  </ChartContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Two-column layout for Top Upsells and Top Cashiers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top Performing Upsells */}
            {upsellMetrics.topUpsells && upsellMetrics.topUpsells.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4">
                    Top Performing Upsells
                  </p>
                  <div className="space-y-3">
                    {upsellMetrics.topUpsells.map((upsell, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-4">{idx + 1}.</span>
                          <span className="text-sm font-medium truncate max-w-[180px]" title={`${upsell.source_product_name} → ${upsell.suggested_product_name}`}>
                            {upsell.source_product_name} → {upsell.suggested_product_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">
                            {upsell.shown_count} shown
                          </span>
                          <span className={cn(
                            "text-sm font-semibold",
                            upsell.conversion_rate >= 20 ? "text-green-500" : 
                            upsell.conversion_rate >= 10 ? "text-amber-500" : "text-muted-foreground"
                          )}>
                            {upsell.conversion_rate.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Upselling Cashiers */}
            {upsellMetrics.topCashiers && upsellMetrics.topCashiers.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4">
                    Top Upselling Cashiers
                  </p>
                  <div className="space-y-3">
                    {upsellMetrics.topCashiers.map((cashier, idx) => (
                      <div key={cashier.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold",
                            idx === 0 ? "bg-amber-100 text-amber-700" :
                            idx === 1 ? "bg-gray-100 text-gray-600" :
                            idx === 2 ? "bg-orange-100 text-orange-700" :
                            "bg-muted text-muted-foreground"
                          )}>
                            {idx + 1}
                          </div>
                          <span className="text-sm font-medium">{cashier.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "text-sm font-semibold",
                            idx === 0 ? "text-amber-600" : "text-foreground"
                          )}>
                            {cashier.upsell_count}
                          </span>
                          <span className="text-xs text-muted-foreground">upsells</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
